import { queryOptions } from '@tanstack/react-query'
import { compareDatesAsc, compareDatesDesc } from 'global/helpers/dates'
import {
  FILE_PREFIX,
  GEOFENCE_PREFIX,
  getRequest,
  postRequest,
  ROUTES_PREFIX,
  TELEMATICS_PREFIX,
  TRIP_PREFIX,
} from 'global/helpers/requests'
import {
  type AvailableStopStatus,
  type Geofence,
  type LegData,
  type ParkingLocation,
  ParkingLocationType,
  type ProReferenceFormValues,
  type ShareParkingLocationViaEmailFormValues,
  type ShareParkingLocationViaSmsFormValues,
  type ShortestRoute,
  type StopData,
  type StopDocument,
  type StopStatus,
  type TripLogData,
  type VehiclePositionMetricValue,
  type VehicleTemperatureMetricValue,
  WorkflowType,
} from './TripDetails.types'

export const TRIP_DETAILS_QUERY_KEY = 'trip-details'
export const TRIP_DETAILS_LEGS_QUERY_KEY = 'legs'
export const TRIP_DETAILS_LOGS_QUERY_KEY = 'logs'
export const TRIP_TELEMATICS_QUERY_KEY = 'trip-telematics'
export const TRIP_TELEMATICS_TEMPERATURES_QUERY_KEY = 'temperatures'
export const TRIP_TELEMATICS_POSITIONS_QUERY_KEY = 'positions'
export const TRIP_TELEMATICS_SHORTEST_ROUTES_QUERY_KEY = 'routes'
export const TRIP_TELEMATICS_PARKING_LOCATIONS_QUERY_KEY = 'locations'
export const TRIP_GEOFENCES_QUERY_KEY = 'trip-geofences'

export const getTripLegs = async (tripCid: string, weblinkToken?: string): Promise<LegData[]> => {
  const legs = await getRequest<LegData[]>(`trips/${tripCid}/shipmentlegs`, { token: weblinkToken }, TRIP_PREFIX)
  return legs.map(processShipmentLeg).sort(sortShipmentLegs)
}

export const tripLegsQueryOptions = (tripCid: string, weblinkToken?: string) => queryOptions({
  queryKey: [TRIP_DETAILS_QUERY_KEY, tripCid, TRIP_DETAILS_LEGS_QUERY_KEY, { weblinkToken }],
  queryFn: () => getTripLegs(tripCid, weblinkToken),
})

export const getTripLogs = async (tripCid: string, weblinkToken?: string): Promise<TripLogData[]> => {
  const events = await getRequest<TripLogData[]>(`trips/${tripCid}/logs`, { token: weblinkToken }, TRIP_PREFIX)
  return events.sort(sortTripLogs)
}

export const tripLogsQueryOptions = (tripCid: string, weblinkToken?: string) => queryOptions({
  queryKey: [TRIP_DETAILS_QUERY_KEY, tripCid, TRIP_DETAILS_LOGS_QUERY_KEY, { weblinkToken }],
  queryFn: () => getTripLogs(tripCid, weblinkToken),
})

export const getFile = async (fileId: string, weblinkToken?: string): Promise<Blob> => {
  const response = await getRequest<Blob>(
    `files/${fileId}`,
    { token: weblinkToken },
    FILE_PREFIX,
  )
  return new Blob([response])
}

export const getTripVehicleTemperatures = async (
  tripCid: string,
  weblinkToken?: string,
): Promise<VehicleTemperatureMetricValue[]> =>
  await getRequest<VehicleTemperatureMetricValue[]>(
    `trips/${tripCid}/temperatures`,
    { token: weblinkToken },
    TELEMATICS_PREFIX,
  )

export const tripVehicleTemperaturesQueryOptions = (tripCid: string, weblinkToken?: string) => queryOptions({
  queryKey: [TRIP_TELEMATICS_QUERY_KEY, tripCid, TRIP_TELEMATICS_TEMPERATURES_QUERY_KEY, { weblinkToken }],
  queryFn: () => getTripVehicleTemperatures(tripCid, weblinkToken),
})

export const getTripVehiclePositions = async (
  tripCid: string,
  weblinkToken?: string,
): Promise<VehiclePositionMetricValue[]> =>
  await getRequest<VehiclePositionMetricValue[]>(
    `trips/${tripCid}/positions`,
    { token: weblinkToken },
    TELEMATICS_PREFIX,
  )

export const tripVehiclePositionsQueryOptions = (tripCid: string, weblinkToken?: string) => queryOptions({
  queryKey: [TRIP_TELEMATICS_QUERY_KEY, tripCid, TRIP_TELEMATICS_POSITIONS_QUERY_KEY, { weblinkToken }],
  queryFn: () => getTripVehiclePositions(tripCid, weblinkToken),
})

export const getTripShortestRoutes = async (
  tripCid: string,
  weblinkToken?: string,
): Promise<ShortestRoute[]> =>
  await getRequest<ShortestRoute[]>(`${tripCid}/routes`, { token: weblinkToken }, ROUTES_PREFIX)

export const tripShortestRoutesQueryOptions = (tripCid: string, weblinkToken?: string) => queryOptions({
  queryKey: [TRIP_TELEMATICS_QUERY_KEY, tripCid, TRIP_TELEMATICS_SHORTEST_ROUTES_QUERY_KEY, { weblinkToken }],
  queryFn: () => getTripShortestRoutes(tripCid, weblinkToken),
})

export const getTripParkingLocations = async (
  tripCid: string,
  weblinkToken?: string,
): Promise<ParkingLocation[]> => {
  const parkingLocations = await getRequest<ParkingLocation[]>(
    `${tripCid}/parking-locations`,
    { token: weblinkToken },
    ROUTES_PREFIX,
  )
  return parkingLocations.map(processParkingLocations)
}

export const tripParkingLocationsQueryOptions = (tripCid: string, weblinkToken?: string) => queryOptions({
  queryKey: [TRIP_TELEMATICS_QUERY_KEY, tripCid, TRIP_TELEMATICS_PARKING_LOCATIONS_QUERY_KEY, { weblinkToken }],
  queryFn: () => getTripParkingLocations(tripCid, weblinkToken),
})

export const shareParkingLocationViaEmail = async (
  tripCid: string,
  parkingLocationId: string,
  data: ShareParkingLocationViaEmailFormValues,
  weblinkToken?: string,
): Promise<void> =>
  await postRequest<void>(
    `${tripCid}/parking-locations/${parkingLocationId}/mail`,
    { body: data, token: weblinkToken },
    ROUTES_PREFIX,
  )

export const shareParkingLocationViaSms = async (
  tripCid: string,
  parkingLocationId: string,
  data: ShareParkingLocationViaSmsFormValues,
  weblinkToken?: string,
): Promise<void> =>
  await postRequest<void>(
    `${tripCid}/parking-locations/${parkingLocationId}/sms`,
    { body: data, token: weblinkToken },
    ROUTES_PREFIX,
  )

export const getTripGeofences = async (
  tripCid: string,
  weblinkToken?: string,
): Promise<Geofence[]> =>
  await getRequest<Geofence[]>(`trips/${tripCid}/geofences`, { token: weblinkToken }, GEOFENCE_PREFIX)

export const tripGeofencesQueryOptions = (tripCid: string, weblinkToken?: string) => queryOptions({
  queryKey: [TRIP_GEOFENCES_QUERY_KEY, tripCid, { weblinkToken }],
  queryFn: () => getTripGeofences(tripCid, weblinkToken),
})

export const updateProReference = async (
  tripCid: string,
  shipmentNumber: string,
  body: ProReferenceFormValues,
  weblinkToken?: string,
) =>
  await postRequest<void>(`trips/${tripCid}/shipmentlegs/${shipmentNumber}/pro-reference`, { body, token: weblinkToken }, TRIP_PREFIX)

export const processShipmentLeg = (leg: LegData): LegData => {
  const defaultAttributes = {
    cargoItemDetails: [],
    dangerousGoodsDetails: [],
    customerReferences: [],
    pupReferences: [],
    delReferences: [],
  }
  leg.attributes = {
    ...defaultAttributes,
    ...leg.attributes,
  }
  leg.wayPoints = leg.wayPoints
    .map(processStatuses)
    .map((stop) => processAvailableStatuses(stop, leg))
    .map(processDocuments)
    .sort(sortStops)
  return leg
}

export const sortShipmentLegs = (a: LegData, b: LegData): number => {
  const aPickup = a.wayPoints.find((stop) => stop.type === 'PUP')
  const bPickup = b.wayPoints.find((stop) => stop.type === 'PUP')
  if (!aPickup || !bPickup) {
    return 0
  }
  // sort by sequence
  const sequence = aPickup.sequence - bPickup.sequence
  if (sequence !== 0) {
    return sequence
  }
  // ...then by early pickup/delivery date
  const date = compareDatesAsc(aPickup.earlyDateTime, bPickup.earlyDateTime)
  if (date !== 0) {
    return date
  }
  // ...and lastly by shipment number
  return a.shipmentNumber.localeCompare(b.shipmentNumber)
}

const processStatuses = (stop: StopData): StopData => {
  // NOTE: "statuses" will not exist when there were no updates
  // NOTE: not all status codes are unique, some depend on additional reason code
  stop.statuses = stop.statuses
    ? stop.statuses
      .map((status) => {
        status.id = getStatusId(status.statusCode, status.reasonCode)
        return status
      })
      .filter((status) => {
        // 3060 - POD uploaded (MACADAM only)
        return !['3060'].includes(status.id)
      })
      .sort(sortStatuses)
    : []
  return stop
}

const processAvailableStatuses = (stop: StopData, leg: LegData): StopData => {
  // NOTE: filter out gate-in, gate-out and POD uploaded statuses
  const hiddenStatuses = [
    '0491', // Gate-out (pickup)
    '2991', // Gate-out (delivery)
    '1567', // Gate-in (customs)
    '1568', // Gate-out (customs)
    '3060', // POD uploaded (MACADAM only)
    // hide extra statuses for non SCD shipments
    ...(leg.workflowType !== WorkflowType.SECURE_CLOUD_DEPLOYMENT ? [
      '0490', // Gate-in (pickup)
      '2990', // Gate-in (delivery)
    ] : []),
  ]
  // NOTE: not all status codes are unique, some depend on additional reason code
  stop.availableStatuses =
    stop.availableStatuses
      ?.filter((status) => !hiddenStatuses.includes(status.code))
      .map((status) => {
        status.id = getStatusId(status.code, status.reasonCode)
        return status
      })
      .sort(sortAvailableStatuses) ?? []
  stop.availableRegularStatuses = stop.availableStatuses.filter((status) => !status.discrepancy)
  stop.availableDiscrepancyStatuses = stop.availableStatuses.filter((status) => status.discrepancy)
  return stop
}

const processDocuments = (stop: StopData): StopData => {
  if (stop.documents) {
    stop.documents.sort(sortDocuments)
  }
  return stop
}

const processParkingLocations = (parkingLocation: ParkingLocation): ParkingLocation => {
  const getParkingLocationType = (): ParkingLocationType => {
    switch (parkingLocation.properties.security_code) {
      case 'F':
        return ParkingLocationType.KUEHNE_NAGEL
      case 'SP':
        return ParkingLocationType.STRATEGIC
      case 'D':
        return ParkingLocationType.SHORT_BREAK_45MIN
      case 'C':
        return ParkingLocationType.BASIC_SECURITY
      case 'B':
        return ParkingLocationType.MEDIUM_SECURITY
      case 'A':
        return ParkingLocationType.HIGH_SECURITY
      case 'NP':
      case 'NA':
        return ParkingLocationType.NEGATIVE_PARKING
      default:
        return ParkingLocationType.OTHER
    }
  }
  parkingLocation.type = getParkingLocationType()
  return parkingLocation
}

export const sortStops = (a: StopData, b: StopData): number => {
  // sort stops by types first in specified order
  const typeOrder = [
    'PUP',
    'DEL',
    'CUS',
  ]
  return typeOrder.indexOf(a.type) - typeOrder.indexOf(b.type)
}

export const sortStatuses = (a: StopStatus, b: StopStatus): number =>
  compareDatesDesc(a.createdAt, b.createdAt)

// sort available statuses in specified order by status code
export const sortAvailableStatuses = (a: AvailableStopStatus, b: AvailableStopStatus): number => {
  // NOTE: keep up-to-date with available statuses
  const statusOrder = [
    '0490', // Gate-in (pickup)
    '0491', // Gate-out (pickup)
    '0500', // Picked up
    '0502_DD01', // Picked up / Short
    '0502_DD02', // Picked up / Damaged
    '0502_C050', // Picked up / Without checks
    '0501_CI09', // Not picked up
    '0501_CI01', // Not picked up / Refused
    '0501_C002', // Not picked up / Shipment not available
    '2990', // Gate-in (delivery)
    '2991', // Gate-out (delivery)
    '2992', // Passed security
    '2993', // Arrived at the dock
    '2994', // On-site team ready
    '2995', // Unload started
    '2996', // Unload completed
    '2997', // Installation started
    '2998', // Installation completed
    '3000', // Delivered
    '3002_DD01', // Delivered / Short
    '3002_DD02', // Delivered / Damaged
    '3001_DI28', // Not delivered
    '3001_DD02', // Not delivered / Damaged
    '3001_DI03', // Not delivered / Refused
    '3001_DI29', // Not delivered / Failed attempt
    '3001_C018', // Not delivered / Mechanical or carrier failure
    '3001_C021', // Not delivered / Missed timeslot
    '1567', // Gate-in (customs)
    '1568', // Gate-out (customs)
    '1500', // Customs entry started
    '1600', // Customs clearance completed
  ]
  return statusOrder.indexOf(a.id) - statusOrder.indexOf(b.id)
}

// make POP and POD always first and rest sorted by date
export const sortDocuments = (a: StopDocument, b: StopDocument): number => {
  const typeOrder = [
    'POP',
    'POD',
  ]
  const fileType = typeOrder.indexOf(a.fileType) - typeOrder.indexOf(b.fileType)
  if (fileType !== 0) {
    return fileType
  }
  return compareDatesAsc(a.createdAt, b.createdAt)
}

export const sortTripLogs = (a: TripLogData, b: TripLogData): number =>
  compareDatesDesc(a.createdAt, b.createdAt)

export const getStatusId = (statusCode: string, reasonCode?: string) =>
  reasonCode ? `${statusCode}_${reasonCode}` : statusCode
